import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';

import { menuLinks } from '@core/constants';
import { ILink } from '@shared/models/links';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthModal, selectAuthUser } from '@state/auth/auth.selectors';
import { selectConfigOpenModal } from '@state/config/config.selectors';
import { selectOrganizationError } from '@state/organization/organization.selectors';

const findRouteParam = (
  route: ActivatedRoute,
  param: string
): string | null => {
  while (route) {
    if (route.snapshot.paramMap.has(param)) {
      return route.snapshot.paramMap.get(param);
    }
    route = route.firstChild!;
  }
  return null;
};

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-shared-layout-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit {
  @Input() guest = false;
  @Input() enableExternal = false;

  protected homeScreen = false;
  protected propertyDetailsScreen = false;

  protected openModal?: string;
  protected openAuthModal?: string;
  protected open = false;
  protected links: ILink[] = menuLinks;
  protected isMobile = false;
  protected isLoggedIn = false;

  protected urlSlug: string | undefined;
  protected organizationError = this.store.select(selectOrganizationError);

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private deviceService: DeviceDetectorService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();

    this.store
      .select(selectConfigOpenModal)
      .pipe(untilDestroyed(this))
      .subscribe((openModal) => {
        this.openModal = openModal;
        this.changeDetectorRef.detectChanges();
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.isLoggedIn = !!user;
      });

    this.store
      .select(selectAuthModal)
      .pipe(untilDestroyed(this))
      .subscribe((openAuthModal) => {
        this.openAuthModal = openAuthModal;
        this.changeDetectorRef.detectChanges();
      });

    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (this.route?.root) {
        const newSlug = findRouteParam(this.route.root, 'slug');
        this.urlSlug = newSlug || undefined;
      } else {
        this.urlSlug = undefined;
      }
    });

    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (
        (this.router.url === '/' ||
          this.router.url.startsWith('/page/') ||
          this.router.url.startsWith('/?')) &&
        !this.router.url.startsWith('/maintenance') &&
        !this.router.url.startsWith('/disclosures')
      ) {
        this.homeScreen = true;
      } else {
        this.homeScreen = false;
      }

      this.propertyDetailsScreen = this.router.url.startsWith('/properties/');
    });
  }

  trackByFn(index: number, link: ILink): string {
    return link.title;
  }

  preventDefault(e: MouseEvent, link: ILink): void {
    if (link.children) {
      e.preventDefault();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.open = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('.links') &&
      !event.target.closest('.container')
    ) {
      this.open = false;
    }
  }
}
